import type { SessionBootstrap } from '@lbc/shared';

const CACHE_KEY_PREFIX = 'lbc.bootstrap.v1.';

interface CachedBootstrap {
  savedAt: number;
  data: SessionBootstrap;
}

export const bootstrapCache = {
  read(groupId: string): CachedBootstrap | null {
    const raw = window.localStorage.getItem(`${CACHE_KEY_PREFIX}${groupId}`);
    if (raw === null) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!isCachedBootstrap(parsed, groupId)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  },

  write(data: SessionBootstrap): void {
    try {
      window.localStorage.setItem(
        `${CACHE_KEY_PREFIX}${data.group.id}`,
        JSON.stringify({ savedAt: Date.now(), data }),
      );
    } catch {
      // A full or unavailable localStorage must never block a server response.
    }
  },
};

function isCachedBootstrap(value: unknown, groupId: string): value is CachedBootstrap {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { savedAt?: unknown; data?: unknown };
  if (typeof candidate.savedAt !== 'number' || !Number.isFinite(candidate.savedAt)) {
    return false;
  }
  if (typeof candidate.data !== 'object' || candidate.data === null) {
    return false;
  }
  const data = candidate.data as {
    group?: unknown;
    participant?: unknown;
    participants?: unknown;
    drinkEvents?: unknown;
  };
  return (
    typeof data.group === 'object' &&
    data.group !== null &&
    (data.group as { id?: unknown }).id === groupId &&
    typeof data.participant === 'object' &&
    data.participant !== null &&
    Array.isArray(data.participants) &&
    Array.isArray(data.drinkEvents)
  );
}
